<!-- ============================================================================
     CALENDAR WRAPPER - Rediseño completo
     Contenedor principal del módulo de citas con header y área de calendario
     ============================================================================ -->

<p-dialog
  [(visible)]="settingsDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [closeOnEscape]="true"
  [style]="{ width: 'min(720px, 96vw)' }"
  styleClass="vc-settings-dialog"
>
  <ng-template pTemplate="header">
    <div class="vc-settings__header vc-settings__header--dialog">
      <i class="fas fa-sliders-h"></i>
      <span>{{ "CALENDAR.SETTINGS.TITLE" | translate }}</span>
    </div>
  </ng-template>

  <div class="vc-settings">
    <div class="vc-settings__body">
      <div class="vc-settings__section">
        <div class="vc-settings__section-header vc-settings__section-header--static">
          <i class="fas fa-calendar-alt"></i>
          <span>{{ "COMMON.CALENDARS" | translate }}</span>
        </div>
        <div class="vc-settings__section-content">
          <app-selector-multi-calendar
            #calendarSelector
          ></app-selector-multi-calendar>
        </div>
      </div>

      <div class="vc-settings__section">
        <div class="vc-settings__section-header vc-settings__section-header--static">
          <i class="fas fa-filter"></i>
          <span>{{ "CALENDAR.SETTINGS.FILTERS.TITLE" | translate }}</span>
          <span *ngIf="hasActiveFilters" class="vc-settings__badge">{{
            activeFiltersCount
          }}</span>
        </div>
        <div class="vc-settings__section-content">
          <p class="vc-settings__hint">
            {{ "CALENDAR.SETTINGS.FILTERS.HINT" | translate }}
          </p>

          <div class="vc-settings__checklist">
            <label
              *ngFor="let filter of settings.filters"
              class="vc-settings__check-item"
              [class.vc-settings__check-item--active]="filter.active"
              [class.vc-settings__check-item--hide]="filter.type === 'hide'"
            >
              <p-checkbox
                [ngModel]="filter.active"
                [binary]="true"
                (ngModelChange)="onFilterChange(filter, $event)"
              ></p-checkbox>
              <i [class]="filter.icon" class="vc-settings__check-icon"></i>
              <span>{{ filter.labelKey | translate }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="vc-settings__section">
        <div class="vc-settings__section-header vc-settings__section-header--static">
          <i class="fas fa-eye"></i>
          <span>{{ "CALENDAR.SETTINGS.VIEW.TITLE" | translate }}</span>
        </div>
        <div class="vc-settings__section-content">
          <div class="vc-settings__checklist">
            <label class="vc-settings__check-item">
              <p-checkbox
                [ngModel]="settings.view.showSundays"
                [binary]="true"
                (ngModelChange)="onViewSettingChange('showSundays', $event)"
              ></p-checkbox>
              <i class="fas fa-calendar-week vc-settings__check-icon"></i>
              <span>{{
                "CALENDAR.SETTINGS.VIEW.SHOW_SUNDAYS" | translate
              }}</span>
            </label>

            <label class="vc-settings__check-item">
              <p-checkbox
                [ngModel]="settings.view.showFullDay"
                [binary]="true"
                (ngModelChange)="onViewSettingChange('showFullDay', $event)"
              ></p-checkbox>
              <i class="fas fa-arrows-alt-v vc-settings__check-icon"></i>
              <span>{{
                "CALENDAR.SETTINGS.VIEW.SHOW_FULL_DAY" | translate
              }}</span>
            </label>

            <label class="vc-settings__check-item">
              <p-checkbox
                [ngModel]="settings.view.showCurrentTimeIndicator"
                [binary]="true"
                (ngModelChange)="
                  onViewSettingChange('showCurrentTimeIndicator', $event)
                "
              ></p-checkbox>
              <i class="fas fa-clock vc-settings__check-icon"></i>
              <span>{{
                "CALENDAR.SETTINGS.VIEW.SHOW_TIME_INDICATOR" | translate
              }}</span>
            </label>

            <label class="vc-settings__check-item">
              <p-checkbox
                [ngModel]="settings.view.dimPastAppointments"
                [binary]="true"
                (ngModelChange)="
                  onViewSettingChange('dimPastAppointments', $event)
                "
              ></p-checkbox>
              <i class="fas fa-history vc-settings__check-icon"></i>
              <span>{{ "CALENDAR.SETTINGS.VIEW.DIM_PAST" | translate }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="vc-settings__section">
        <div class="vc-settings__section-header vc-settings__section-header--static">
          <i class="fas fa-palette"></i>
          <span>{{ "CALENDAR.SETTINGS.DISPLAY.TITLE" | translate }}</span>
        </div>
        <div class="vc-settings__section-content">
          <div class="vc-settings__checklist">
            <label class="vc-settings__check-item">
              <p-checkbox
                [ngModel]="settings.display.compactCards"
                [binary]="true"
                (ngModelChange)="onDisplaySettingChange('compactCards', $event)"
              ></p-checkbox>
              <i class="fas fa-compress-alt vc-settings__check-icon"></i>
              <span>{{
                "CALENDAR.SETTINGS.DISPLAY.COMPACT_CARDS" | translate
              }}</span>
            </label>

            <label class="vc-settings__check-item">
              <p-checkbox
                [ngModel]="settings.display.showDuration"
                [binary]="true"
                (ngModelChange)="onDisplaySettingChange('showDuration', $event)"
              ></p-checkbox>
              <i class="fas fa-hourglass-half vc-settings__check-icon"></i>
              <span>{{
                "CALENDAR.SETTINGS.DISPLAY.SHOW_DURATION" | translate
              }}</span>
            </label>

            <label class="vc-settings__check-item">
              <p-checkbox
                [ngModel]="settings.display.showStatusIcons"
                [binary]="true"
                (ngModelChange)="
                  onDisplaySettingChange('showStatusIcons', $event)
                "
              ></p-checkbox>
              <i class="fas fa-icons vc-settings__check-icon"></i>
              <span>{{
                "CALENDAR.SETTINGS.DISPLAY.SHOW_STATUS_ICONS" | translate
              }}</span>
            </label>
          </div>

          <!-- Selector de modo de color -->
          <div class="vc-settings__color-mode">
            <label class="vc-settings__color-mode-label">
              <i class="fas fa-palette"></i>
              {{ "CALENDAR.SETTINGS.DISPLAY.COLOR_MODE.LABEL" | translate }}
            </label>
            <div class="vc-settings__color-mode-options">
              <button
                type="button"
                class="vc-settings__color-mode-btn"
                [class.vc-settings__color-mode-btn--active]="
                  settings.display.colorMode === 'treatment'
                "
                (click)="onDisplaySettingChange('colorMode', 'treatment')"
              >
                <i class="fas fa-tooth"></i>
                {{
                  "CALENDAR.SETTINGS.DISPLAY.COLOR_MODE.TREATMENT" | translate
                }}
              </button>
              <button
                type="button"
                class="vc-settings__color-mode-btn"
                [class.vc-settings__color-mode-btn--active]="
                  settings.display.colorMode === 'status'
                "
                (click)="onDisplaySettingChange('colorMode', 'status')"
              >
                <i class="fas fa-flag"></i>
                {{ "CALENDAR.SETTINGS.DISPLAY.COLOR_MODE.STATUS" | translate }}
              </button>
            </div>
            <p class="vc-settings__color-mode-hint">
              {{ "CALENDAR.SETTINGS.DISPLAY.COLOR_MODE.HINT" | translate }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="vc-settings__footer">
      <button
        pButton
        type="button"
        icon="fas fa-check"
        label="Aceptar"
        (click)="closeSettingsDialog()"
      ></button>
    </div>
  </ng-template>
</p-dialog>

<!-- Contenedor principal simplificado -->
<div class="vc-calendar-container">
  <!-- Mensaje de advertencia si no hay calendarios -->
  <div class="vc-calendar-warning" *ngIf="!!!this.calendars && !!!this.loading">
    <p-messages [escape]="false" severity="warn">
      <ng-template pTemplate>
        <div class="vc-calendar-warning__content">
          <i class="fas fa-exclamation-triangle"></i>
          <div>
            <strong>{{ "COMMON.WARNING" | translate }}</strong>
            <p>{{ "CALENDAR.EMPTY" | translate }}</p>
          </div>
        </div>
      </ng-template>
    </p-messages>
  </div>

  <!-- Área del calendario (sin recuadro adicional) -->
  <app-calendar-appointments
    #appointmentsCalendar
    *ngIf="this.calendars"
    class="vc-calendar-content"
    [settings]="this.settings"
    [date]="this.date"
    [patient]="this.patient"
    [treatments]="this.treatments"
    [quickMode]="this.quickMode"
    [calendar]="calendarSelector.getCalendars()"
    [openSettings]="openSettingsDialog.bind(this)"
  ></app-calendar-appointments>
</div>
